// src/oura/merchant/WorkList.tsx
//
// The merchant's work list, drawn once (27 Oct 2026): Our work's "What we
// are doing". To do's With us and Home point here rather than drawing it again.
//
// One list, no boxes, grouped by the areas a client recognises (Your
// website, New pages, Google and listings, Checks and reports). The groups
// flow into two columns that even themselves out for each client, and a
// group never splits across them (Jack, 27 Oct 2026: area cards left a short
// group stranded and the columns lopsided). In a group: Needs you first,
// Done last. A line wears its item's icon tinted for its status (WorkMarks)
// and opens in place to say what it is, where it stands, what we fixed and
// when. A line that needs them says so in amber and offers its question. On
// a phone, an area's Not started and Done lines fold behind one row.

import { CheckIcon, ChevronDownIcon, ClipboardListIcon } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { ClientPagePayload, ClientPlanItem, ClientPlanStatus, Thread } from "@service/aeo/clientReview";
import { CardEmpty, FOCUS_RING, LATE_TEXT, NARROW_VIEWPORT, PillButton, TYPE, useMediaQuery } from "@oura/ui";
import { cn } from "../lib/cn";
import { askFact, type Ask } from "./asks";
import { measuredSentence } from "./drawers/WorkDrawer";
import { WorkProgress } from "./HomeMonth";
import { lineDescription, lineWhen, measuredItems, openAskFor } from "./model";
import { PhoneFoldCard } from "./PhoneFold";
import { ItemIcon } from "./WorkMarks";
import { areaOf, WORK_AREAS } from "./workDates";

/** Folded behind one row per area on a phone. */
const FOLDED_ON_PHONE: ClientPlanStatus[] = ["not_started", "done"];

/** "Show 2 done and 1 not started". */
function foldedWords(lines: ClientPlanItem[]): string {
    const done = lines.filter((i) => i.status === "done").length;
    const later = lines.length - done;
    const parts = [done > 0 ? `${done} done` : "", later > 0 ? `${later} not started` : ""].filter(Boolean);
    return `Show ${parts.join(" and ")}`;
}

/** A group's order: theirs first, finished last. */
const LINE_ORDER: ClientPlanStatus[] = ["waiting_on_you", "in_progress", "with_google", "reported_by_hand", "not_started", "done"];

/** Our work's card: the progress line, then the list. On a phone it folds by its head. */
export function WorkCard(props: {
    items: ClientPlanItem[];
    schedule: ClientPagePayload["schedule"];
    asks: Ask[];
    threadOf: (itemId: string) => Thread | null;
    engagement: ClientPagePayload["engagement"];
    onAnswer: (askId: string) => void;
    /** A line to open on arrival (Home's month card links here with `?line=`). */
    openKey?: string | null;
}) {
    const measured = measuredItems(props.items);
    const done = measured.filter((i) => i.status === "done").length;
    return (
        <PhoneFoldCard anchor="this-month" title="What we are doing" foldedFact={measured.length > 0 ? `${done} of ${measured.length} done` : undefined}>
            {props.items.length === 0 ? (
                <CardEmpty icon={ClipboardListIcon} title="No work scheduled yet">
                    Your account manager is preparing your plan. This fills in the moment it is activated.
                </CardEmpty>
            ) : (
                <>
                    <WorkProgress items={props.items} schedule={props.schedule} />
                    <WorkList {...props} />
                </>
            )}
        </PhoneFoldCard>
    );
}

/** The list alone, grouped by area. */
export function WorkList({
    items,
    asks,
    threadOf,
    engagement,
    onAnswer,
    openKey = null,
}: {
    items: ClientPlanItem[];
    asks: Ask[];
    threadOf: (itemId: string) => Thread | null;
    engagement: ClientPagePayload["engagement"];
    onAnswer: (askId: string) => void;
    openKey?: string | null;
}) {
    // Any number open at once, to compare two lines.
    const [openKeys, setOpenKeys] = useState<ReadonlySet<string>>(() => new Set());
    const narrow = useMediaQuery(NARROW_VIEWPORT);
    // On a phone, each area's Not started and Done lines start folded behind
    // one row: the list is about what is moving (27 Oct 2026).
    const [unfolded, setUnfolded] = useState<ReadonlySet<string>>(() => new Set());
    // A line linked from Home opens, its area's fold with it, and comes into view.
    useEffect(() => {
        if (!openKey) return;
        const item = items.find((i) => (i.key ?? i.code) === openKey || i.code === openKey);
        if (!item) return;
        const key = item.key ?? item.code;
        setOpenKeys((was) => new Set(was).add(key));
        setUnfolded((was) => new Set(was).add(areaOf(item.code)));
        requestAnimationFrame(() => document.querySelector(`[data-plan-key="${CSS.escape(key)}"]`)?.scrollIntoView({ block: "center" }));
    }, [openKey, items]);
    const toggle = (key: string) =>
        setOpenKeys((was) => {
            const next = new Set(was);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    // Needs you first, then what is moving, what is next, and Done last, in each group.
    const sorted = useMemo(() => {
        const rank = (st: ClientPlanStatus) => LINE_ORDER.indexOf(st);
        return items.map((item, i) => ({ item, i })).sort((a, b) => rank(a.item.status) - rank(b.item.status) || a.i - b.i).map(({ item }) => item);
    }, [items]);
    // A name that repeats (a monthly line still open from an earlier month) says its month.
    const repeated = useMemo(() => {
        const seen = new Map<string, number>();
        for (const i of items) seen.set(i.label, (seen.get(i.label) ?? 0) + 1);
        return new Set(Array.from(seen).filter(([, n]) => n > 1).map(([label]) => label));
    }, [items]);
    const line = (item: ClientPlanItem) => {
        const key = item.key ?? item.code;
        const askId = item.status === "waiting_on_you" ? openAskFor(item, asks) : null;
        const ask = askId ? asks.find((a) => a.item.id === askId) ?? null : null;
        return (
            <WorkLine
                key={key}
                item={item}
                engagement={engagement}
                askId={askId}
                fact={ask ? askFact(ask, threadOf(ask.item.id)) : null}
                showMonth={repeated.has(item.label)}
                open={openKeys.has(key)}
                onToggle={() => toggle(key)}
                onAnswer={onAnswer}
            />
        );
    };
    return (
        <div className="gap-x-8 md:columns-2">
            {WORK_AREAS.map((a) => {
                const list = sorted.filter((i) => areaOf(i.code) === a.key);
                if (list.length === 0) return null;
                const done = list.filter((i) => i.status === "done").length;
                const quiet = narrow && !unfolded.has(a.key) ? list.filter((i) => FOLDED_ON_PHONE.includes(i.status)) : [];
                const shown = quiet.length > 0 ? list.filter((i) => !quiet.includes(i)) : list;
                return (
                    <section key={a.key} aria-label={a.label} data-group-head={`work.${a.key}`} className="break-inside-avoid pb-4">
                        <p className={cn(TYPE.lab, "m-0 flex items-baseline justify-between gap-3 pb-1")}>
                            <span>{a.label}</span>
                            <span className="tabular-nums text-gray-400">
                                {done} of {list.length} done
                            </span>
                        </p>
                        {shown.length > 0 && <ul className="list-plain m-0 p-0">{shown.map(line)}</ul>}
                        {quiet.length > 0 && (
                            <button
                                type="button"
                                aria-expanded={false}
                                onClick={() => setUnfolded((was) => new Set(was).add(a.key))}
                                className={cn(TYPE.tc, "flex min-h-[44px] w-full items-center gap-3 rounded-lg px-2 text-left text-gray-500 hover:bg-gray-50", FOCUS_RING)}
                            >
                                <span aria-hidden className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-gray-50 text-gray-400">
                                    <ChevronDownIcon size={15} />
                                </span>
                                {foldedWords(quiet)}
                            </button>
                        )}
                    </section>
                );
            })}
        </div>
    );
}

/** One line, and what it says when it is opened, under it. */
function WorkLine({
    item,
    engagement,
    askId,
    fact,
    showMonth,
    open,
    onToggle,
    onAnswer,
}: {
    item: ClientPlanItem;
    engagement: ClientPagePayload["engagement"];
    askId: string | null;
    fact: ReturnType<typeof askFact>;
    showMonth: boolean;
    open: boolean;
    onToggle: () => void;
    onAnswer: (askId: string) => void;
}) {
    const what = lineDescription(item, engagement);
    const when = lineWhen(item, engagement);
    const measured = measuredSentence(item);
    const panelId = `work-line-${item.key ?? item.code}`;
    const sub =
        item.status === "waiting_on_you" ? (
            <span className={cn(TYPE.tc, "mt-0.5 block font-medium", fact?.late ? LATE_TEXT : "text-amber-700")}>{fact?.late ? fact.text : askId ? "Needs your answer" : "Needs you"}</span>
        ) : showMonth && item.month ? (
            <span className={cn(TYPE.tc, "mt-0.5 block text-gray-500")}>Month {item.month}</span>
        ) : null;
    return (
        <li className={cn("break-inside-avoid rounded-lg transition-colors", open ? "bg-gray-50" : "hover:bg-gray-50")}>
            <button
                type="button"
                data-plan-code={item.code}
                data-plan-key={item.key ?? item.code}
                aria-expanded={open}
                aria-controls={panelId}
                onClick={onToggle}
                className={cn("flex min-h-[44px] w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left", FOCUS_RING)}
            >
                <ItemIcon code={item.code} status={item.status} />
                <span className="min-w-0 flex-1">
                    <span className={cn(TYPE.tb, "block break-words", item.status === "not_started" ? "text-gray-600" : "text-gray-900")}>{item.label}</span>
                    {sub}
                </span>
                <ChevronDownIcon
                    aria-hidden
                    size={16}
                    className={cn("shrink-0 text-gray-400 transition-transform duration-200 motion-reduce:transition-none", open && "rotate-180")}
                />
            </button>
            {open && (
                <div id={panelId} className="space-y-3 pb-3 pl-12 pr-2 pt-1">
                    {what && <Block label="What it is">{what}</Block>}
                    {item.line && (
                        <Block label="Where it stands">
                            {item.line}
                            {item.month ? ` Part of month ${item.month}.` : ""}
                        </Block>
                    )}
                    {item.fixed && item.fixed.length > 0 && (
                        <Block label="What we fixed">
                            <ul className="list-plain m-0 p-0">
                                {item.fixed.map((f) => (
                                    <li key={f} className="flex items-start gap-2">
                                        <CheckIcon aria-hidden size={14} className="mt-0.5 shrink-0 text-emerald-600" />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                        </Block>
                    )}
                    {when && <Block label="When">{when}</Block>}
                    {measured && <Block label="What we check">{measured}</Block>}
                    {askId && (
                        <PillButton variant="secondary" size="sm" onClick={() => onAnswer(askId)}>
                            Answer now
                        </PillButton>
                    )}
                </div>
            )}
        </li>
    );
}

/** A labelled paragraph inside an opened line. */
export function Block({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div>
            <p className={cn(TYPE.lab, "m-0")}>{label}</p>
            <div className={cn(TYPE.tb, "mt-0.5 text-gray-700")}>{children}</div>
        </div>
    );
}
